import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  useCurrentUser,
  useDispatch,
  useFile,
  useNote,
  usePracticeUsers,
  useProjectFromNoteId,
} from '@witmetrics/data-store';
import { mapToKey } from '@witmetrics/utils/arrays';
import { isEmpty } from '@witmetrics/utils/misc';
import { getProjectDisplayName } from '@witmetrics/unison-core';
import { API } from '@/api';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { usePath } from '@/hooks/usePath';
import { showErrorToaster } from '@/utils/toasters';
import { scrollIntoView } from '@/utils/dom';
import { useArray } from '@/hooks/useArray';
import { useString } from '@/hooks/useString';
import Card from '@/components/Card';
import Note from '@/components/Note';
import NotesField from '@/components/inputs/NotesField';
import FileCategoryIcon from '@/components/FileCategoryIcon';
import CaseTypeIcon from '@/components/CaseTypeIcon';
import AttachmentsList, { Attachment } from '@/components/AttachmentsList';
import DeleteNoteDialog from '@/dialogs/DeleteNoteDialog';
import DeleteAttachmentDialog from '@/dialogs/DeleteAttachmentDialog';
import AttachmentPreviewDialog from '@/dialogs/AttachmentPreviewDialog';
import NoteBlockHeader from './NoteBlockHeader';
import CommentsSection from './CommentsSection';
import type { LoadedAttachment } from './types';
import type { TimelineVariant } from '../types';
import {
  classes,
  isAuthor,
  isHighlighted,
  loadAttachments,
  styles,
} from './utils';

export type NoteBlockProps = {
  showProject?: boolean;
  editable?: boolean;
  variant?: TimelineVariant;
  noteId: number;
  fileId?: number;
  onOpenFile?: (fileId: number) => void;
};

export default function NoteBlock({
  showProject,
  editable,
  variant = 'default',
  noteId,
  fileId,
  onOpenFile,
}: NoteBlockProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { onApiError, onSelectProject } = useAppState();
  const { searchParams } = usePath();
  const currentUser = useCurrentUser();
  const practiceUsers = usePracticeUsers();
  const note = useNote(noteId);
  const file = useFile(fileId);
  const project = useProjectFromNoteId(noteId);
  const [attachments, setAttachments] = useArray<LoadedAttachment>();
  const [activeAttachment, setActiveAttachment] =
    useState<LoadedAttachment | null>(null);
  const [content, setContent, clearContent] = useString();
  const [isEditing, toggleEditing] = useToggle();
  const [isSaving, toggleSaving] = useToggle();
  const [isLoadingAttachments, toggleLoadingAttachments] = useToggle();
  const [isDeletingAttachment, toggleDeletingAttachment] = useToggle();
  const [isDeleteNoteDialogOpen, toggleDeleteNoteDialog] = useToggle();
  const [isDeleteAttachmentDialogOpen, toggleDeleteAttachmentDialog] =
    useToggle();
  const [isPreviewDialogOpen, togglePreviewDialog] = useToggle();
  const isEditable = editable && isAuthor(note, currentUser?.id);

  useLayoutEffect(() => {
    if (isHighlighted(note, searchParams)) scrollIntoView(wrapperRef.current);
  }, [searchParams]);

  useEffect(() => {
    handleLoadAttachments();
  }, [note]);

  const handleLoadAttachments = async () => {
    try {
      toggleLoadingAttachments(true);
      setAttachments(await loadAttachments(note));
      toggleLoadingAttachments(false);
    } catch (err) {
      toggleLoadingAttachments(false);
      onApiError(err, 'Error loading attachments');
    }
  };

  const handleEditNote = () => {
    setContent(note!.content);
    toggleEditing(true);
  };

  const handleCancelEdit = () => {
    toggleEditing(false);
    clearContent();
  };

  const handlePreviewAttachment = (attachment: LoadedAttachment) => {
    setActiveAttachment(attachment);
    togglePreviewDialog(true);
  };

  const handleClosePreviewDialog = () => {
    togglePreviewDialog(false);
    setActiveAttachment(null);
  };

  const handleStartDeleteAttachment = (attachment: LoadedAttachment) => {
    setActiveAttachment(attachment);
    toggleDeleteAttachmentDialog(true);
  };

  const handleDeleteAttachment = async () => {
    try {
      toggleDeletingAttachment(true);
      const fileId = activeAttachment?.noteFile.fileId;
      if (!fileId) {
        showErrorToaster('Error removing attachment');
        toggleDeletingAttachment(false);
        toggleDeleteAttachmentDialog(false);
        return;
      }
      await API.Files.removeFile(fileId);
      setAttachments((a) =>
        a.filter(({ noteFile }) => noteFile.fileId !== fileId)
      );
      dispatch.deleteFile({ fileId });
      toggleDeleteAttachmentDialog(false);
      toggleDeletingAttachment(false);
      setActiveAttachment(null);
    } catch (err) {
      toggleDeletingAttachment(false);
      onApiError(err, 'Error removing attachment');
    }
  };

  const handleSave = async (value: string) => {
    try {
      toggleSaving(true);
      const updatedNote = await API.Notes.updateNote(noteId, {
        content: value,
      });
      dispatch.addNotes([updatedNote]);
      toggleSaving(false);
      toggleEditing(false);
      setContent(value);
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving note');
    }
  };

  if (!note) return null;

  return (
    <>
      {isDeleteNoteDialogOpen && (
        <DeleteNoteDialog
          noteId={noteId}
          onClose={() => toggleDeleteNoteDialog()}
        />
      )}
      {isDeleteAttachmentDialogOpen && activeAttachment && (
        <DeleteAttachmentDialog
          isDeleting={isDeletingAttachment}
          onDelete={handleDeleteAttachment}
          onClose={handleClosePreviewDialog}
        />
      )}
      {isPreviewDialogOpen && activeAttachment && (
        <AttachmentPreviewDialog
          name={activeAttachment.noteFile.file.name}
          src={activeAttachment.source}
          onClose={handleClosePreviewDialog}
        />
      )}
      <div ref={wrapperRef}>
        <Card
          sx={
            variant === 'aurora'
              ? { ...styles.card, ...styles.auroraDark }
              : styles.card
          }
          className={twMerge(
            classes.card,
            variant === 'aurora' && classes.auroraCard,
            isHighlighted(note, searchParams) && classes.highlighted
          )}>
          <div className={classes.contentWrapper}>
            <NoteBlockHeader
              editable={isEditable}
              note={note}
              onEdit={handleEditNote}
              onDelete={() => toggleDeleteNoteDialog(true)}
            />
            {showProject && project && (
              <div className={classes.linkWrapper}>
                <CaseTypeIcon
                  className={classes.caseType}
                  projectTypeId={project.unisonProjectTypeId}
                  iconClassName={classes.caseTypeIcon}
                />
                <button
                  className={classes.linkName}
                  onClick={() => onSelectProject(project.id)}>
                  {getProjectDisplayName(project)}
                  {!isEmpty(project.externalRef) && ` (${project.externalRef})`}
                </button>
              </div>
            )}
            {file && (
              <div className={classes.linkWrapper}>
                <FileCategoryIcon file={file} size="xs" />
                <button
                  className={classes.linkName}
                  onClick={() => onOpenFile?.(file.id)}>
                  {file.name}
                </button>
              </div>
            )}
            <div className={classes.notesWrapper}>
              {isEditing ? (
                <NotesField
                  isSaving={isSaving}
                  className={classes.notesField}
                  initialMessage={content}
                  placeholder="Add a note, @mention a user..."
                  users={mapToKey(practiceUsers, 'user')}
                  onSave={handleSave}
                  onCancel={handleCancelEdit}
                />
              ) : (
                <Note className={classes.noteBody} value={note.content} />
              )}
            </div>
            {!isEmpty(attachments) && (
              <AttachmentsList>
                {attachments.map((a) => (
                  <Attachment
                    key={a.noteFile.fileId}
                    src={a.previewSource}
                    alt={a.noteFile.file.name}
                    onPreview={() => handlePreviewAttachment(a)}
                    onDelete={
                      isEditable
                        ? () => handleStartDeleteAttachment(a)
                        : undefined
                    }
                  />
                ))}
              </AttachmentsList>
            )}
          </div>
          <CommentsSection note={note} />
        </Card>
      </div>
    </>
  );
}
